import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, Users, ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { fmtTime12, fmtDateLong } from "@/lib/api";

export default function Themes() {
  const c = useContent().pages.themes;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/workshops", { params: { is_theme: true } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <div className="max-w-3xl">
        <div className="text-terracotta text-sm tracking-widest uppercase font-bold">{c.eyebrow}</div>
        <h1 className="mt-2 font-serif text-5xl md:text-6xl leading-tight">{c.heading} <span className="text-forest">{c.heading_accent}</span></h1>
        <p className="mt-4 text-lg text-charcoal/75 font-medium">{c.description}</p>
      </div>

      {loading ? (
        <div className="mt-16 text-center text-charcoal/50">Loading themes…</div>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center bg-white rounded-3xl p-16 shadow-card">
          <h3 className="font-serif text-2xl">No themed sessions available right now.</h3>
          <p className="mt-2 text-charcoal/70">Check back soon — we add new themes regularly, or <Link to="/workshops" className="text-terracotta font-semibold">book a regular workshop</Link>.</p>
        </div>
      ) : (
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((w) => (
            <div key={w.id} className="bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-hover transition-transform hover:-translate-y-1" data-testid={`theme-card-${w.id}`}>
              {w.photo_url && <img src={w.photo_url} alt={w.name} className="h-60 w-full object-cover"/>}
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-ochre/15 text-ochre text-xs font-bold uppercase tracking-wider">Theme</span>
                  {w.featured && <span className="px-3 py-1 rounded-full bg-terracotta/15 text-terracotta text-xs font-bold">Featured</span>}
                  {w.sold_out || w.spots_remaining === 0 ? (
                    <span className="px-3 py-1 rounded-full bg-charcoal text-white text-xs font-bold">Sold Out</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-forest/15 text-forest text-xs font-bold">Available</span>
                  )}
                </div>
                <h3 className="mt-4 font-serif text-2xl leading-tight">{w.name}</h3>
                <p className="mt-2 text-sm text-charcoal/70 line-clamp-3">{w.description}</p>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-charcoal-muted">
                  <div className="flex items-center gap-1.5"><Clock size={14}/> {w.multiple_dates ? "Multiple dates available" : <>{fmtDateLong(w.date, { month: 'short', day: 'numeric' })} · {fmtTime12(w.start_time)}</>}</div>
                  <div className="flex items-center gap-1.5"><Users size={14}/> Ages {w.age_requirement}</div>
                </div>
                <Link to={`/workshops/${w.id}`} data-testid={`book-theme-${w.id}`}
                  className={`mt-5 flex items-center justify-center gap-2 rounded-full px-5 py-3 font-bold transition-transform hover:-translate-y-0.5 ${w.sold_out || w.spots_remaining === 0 ? "bg-cream-alt text-charcoal-muted cursor-not-allowed pointer-events-none" : "bg-terracotta text-white shadow-button"}`}>
                  {w.sold_out || w.spots_remaining === 0 ? "Sold Out" : <>Book This Theme <ArrowRight size={16}/></>}
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
